import { ArrowUpward, DeleteOutline } from '@mui/icons-material';
import styled from '@emotion/styled';
import { uiTheme as t, EmptyState, StatusIndicator, Checkbox } from 'Components/ui';
import type { CourseMemberI, MembershipTab } from 'Api/axios/course-members-api';

export interface MembersTableLabels {
  employee: string;
  jobTitle: string;
  department: string;
  terminal: string;
  /** Encabezado de la quinta columna: "Origen" en obligatorios, "Estado" en opcionales. */
  lastColumn: string;
  originManual: string;
  originAssigned: string;
  stateNotStarted: string;
  stateInProgress: string;
  stateApproved: string;
  stateFailed: string;
  remove: string;
  /** Por qué no se puede quitar de opcionales a quien ya empezó. Va en el `title`. */
  removeBlocked: string;
  moveToMandatory: string;
  empty: string;
}

interface MembersTableProps {
  rows: CourseMemberI[];
  tab: MembershipTab;
  labels: MembersTableLabels;
  /** Oculta casillas y acciones cuando la pestaña es de solo lectura. */
  canManage: boolean;
  selected: Set<string>;
  onToggle: (userId: string) => void;
  onToggleAll: () => void;
  onRemove: (userId: string) => void;
  onMove: (userId: string) => void;
}

/**
 * Estado del miembro opcional: color del punto y etiqueta a mostrar.
 *
 * Reprobado va en rojo porque es un desenlace, no un trámite pendiente. El
 * resto reutiliza los dos tonos de siempre.
 */
const STATES = {
  not_started: { tone: 'inactive', label: 'stateNotStarted' },
  in_progress: { tone: 'active', label: 'stateInProgress' },
  approved: { tone: 'active', label: 'stateApproved' },
  failed: { tone: 'danger', label: 'stateFailed' },
} as const;

const GRID = '38px 2.3fr 1.5fr 1.5fr 1.2fr 1fr 76px';

const Surface = styled.div({
  marginTop: 20,
  background: t.color.surface,
  border: `1px solid ${t.color.border}`,
  borderRadius: t.radius.lg,
  overflow: 'hidden',
  boxShadow: t.shadow.card,
});

const HeadRow = styled.div({
  display: 'grid',
  gridTemplateColumns: GRID,
  alignItems: 'center',
  gap: 16,
  padding: '14px 24px',
  background: t.color.tableHeadBg,
  borderBottom: `1px solid ${t.color.rowBorder}`,
});

const HeadCell = styled.span({
  fontFamily: t.font,
  fontSize: 11.5,
  fontWeight: 600,
  letterSpacing: '0.6px',
  color: t.color.tableHeadText,
});

const BodyRow = styled.div({
  display: 'grid',
  gridTemplateColumns: GRID,
  alignItems: 'center',
  gap: 16,
  padding: '15px 24px',
  borderTop: `1px solid ${t.color.rowBorder}`,
  '&:hover': { background: t.color.rowHoverBg },
});

const Name = styled.div({
  fontFamily: t.font,
  fontSize: 14.5,
  fontWeight: 600,
  color: t.color.title,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const Payroll = styled.div({
  fontFamily: t.font,
  fontSize: 12.5,
  color: t.color.muted,
});

const Cell = styled.div({
  fontFamily: t.font,
  fontSize: 13.5,
  color: t.color.text,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const Actions = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 4,
});

const IconButton = styled.button<{ hoverColor: string; hoverBg: string }>(({ hoverColor, hoverBg }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  background: 'transparent',
  color: t.color.muted,
  cursor: 'pointer',
  padding: 6,
  borderRadius: t.radius.xs,
  transition: 'color 0.15s ease, background 0.15s ease',
  '& .MuiSvgIcon-root': { fontSize: 20 },
  '&:hover': { color: hoverColor, background: hoverBg },
  '&:disabled': { opacity: 0.35, cursor: 'not-allowed' },
  '&:disabled:hover': { color: t.color.muted, background: 'transparent' },
}));

/**
 * Organismo: tabla de miembros del curso.
 *
 * La quinta columna cambia con la pestaña y no es decorativa. En obligatorios
 * muestra el origen, que dice si borrar al empleado sirve de algo o lo repone
 * el siguiente guardado en lote. En opcionales muestra el estado, que dice si
 * quitarlo significa algo: a quien ya empezó no le quita nada.
 *
 * No hay columna de avance a propósito: para eso están los reportes.
 */
const MembersTable = ({
  rows,
  tab,
  labels,
  canManage,
  selected,
  onToggle,
  onToggleAll,
  onRemove,
  onMove,
}: MembersTableProps) => {
  const allSelected = rows.length > 0 && rows.every(row => selected.has(row.userId));

  // La invitación solo decide algo mientras no haya fila de inscripción. En
  // cuanto la hay, es ella la que manda —en esta lista, en el botón de iniciar
  // y en la elegibilidad—, así que quitar la invitación no saca a nadie ni le
  // retira el acceso: es un no-op que el backend rechaza con 409. Se apaga la
  // acción en vez de ofrecerla y reportar un éxito que no ocurre.
  //
  // La casilla sigue viva a propósito: mover a obligatorios sí les aplica, y es
  // el primer paso para sacarlos del curso de verdad.
  const canRemove = (row: CourseMemberI) => tab !== 'optional' || !row.hasEnrollment;

  const lastCell = (row: CourseMemberI) => {
    if (tab === 'mandatory') {
      return <Cell>{row.origin === 'manual' ? labels.originManual : labels.originAssigned}</Cell>;
    }

    const state = STATES[row.state] ?? STATES.not_started;

    return <StatusIndicator status={state.tone} label={labels[state.label]} />;
  };

  return (
    <Surface>
      <HeadRow>
        {canManage ? <Checkbox checked={allSelected} onChange={onToggleAll} aria-label={labels.employee} /> : <span />}
        <HeadCell>{labels.employee}</HeadCell>
        <HeadCell>{labels.jobTitle}</HeadCell>
        <HeadCell>{labels.department}</HeadCell>
        <HeadCell>{labels.terminal}</HeadCell>
        <HeadCell>{labels.lastColumn}</HeadCell>
        <span />
      </HeadRow>

      {rows.map(row => (
        <BodyRow key={row.userId}>
          {canManage ? (
            <Checkbox
              checked={selected.has(row.userId)}
              onChange={() => onToggle(row.userId)}
              aria-label={row.fullName}
            />
          ) : (
            <span />
          )}
          <div>
            <Name>{row.fullName}</Name>
            <Payroll>{row.payrollNum ?? '—'}</Payroll>
          </div>
          <Cell>{row.jobTitleName || '—'}</Cell>
          <Cell>{row.departmentName || '—'}</Cell>
          <Cell>{row.terminalName || '—'}</Cell>
          {lastCell(row)}
          {canManage ? (
            <Actions>
              {tab === 'optional' ? (
                <IconButton
                  type="button"
                  onClick={() => onMove(row.userId)}
                  aria-label={labels.moveToMandatory}
                  hoverColor={t.color.primary}
                  hoverBg={t.color.countBg}
                >
                  <ArrowUpward />
                </IconButton>
              ) : null}
              <IconButton
                type="button"
                onClick={() => onRemove(row.userId)}
                disabled={!canRemove(row)}
                title={canRemove(row) ? undefined : labels.removeBlocked}
                aria-label={canRemove(row) ? labels.remove : labels.removeBlocked}
                hoverColor={t.color.danger}
                hoverBg={t.color.dangerSurface}
              >
                <DeleteOutline />
              </IconButton>
            </Actions>
          ) : (
            <span />
          )}
        </BodyRow>
      ))}

      {rows.length === 0 ? <EmptyState message={labels.empty} /> : null}
    </Surface>
  );
};

export default MembersTable;
